/* ========================================
   vc.products.js – VapeChamp Product Page JavaScript
   Horizon v3.0 – Upgrade-Safe
   Product-specific features that also run in Quick-Add Modal
   ======================================== */


/* ========================================
   1. DELIVERY STATUS SYSTEM
   ======================================== */

const DeliveryStatus = (() => {
  function getStockLevel(container, variantId) {
    let stock = 999;

    // Methode 1: Product Inventory Element (Produktseite)
    const inventoryElement = container.querySelector('product-inventory');
    const inventoryText = inventoryElement?.textContent?.trim() || '';

    if (inventoryText) {
      if (inventoryText.includes('Nicht vorrätig') ||
          inventoryText.includes('Ausverkauft') ||
          inventoryText.includes('Out of stock')) {
        stock = 0;
      } else {
        const stockMatch = inventoryText.match(/(\d+)/);
        stock = stockMatch ? parseInt(stockMatch[1], 10) : 999;
      }
    } else {
      // Methode 2: Shopify Product JSON (Modal)
      const modalProductScript = container.querySelector('script[type="application/json"]');

      if (modalProductScript) {
        try {
          const variantData = JSON.parse(modalProductScript.textContent);

          if (variantData.id && variantData.id.toString() === variantId) {
            stock = variantData.available === false ? 0 : 999;
          }
        } catch (e) {
          // Fehler beim Parsen - verwende Fallback
        }
      }
    }

    return stock;
  }

  function determineStatus(stock, isEOL) {
    if (isEOL && stock <= 0) {
      return {
        class: 'vc-delivery-status--eol',
        text: 'Nicht mehr lieferbar'
      };
    } else if (stock <= 0) {
      return {
        class: 'vc-delivery-status--out-of-stock',
        text: 'Kommt bald wieder'
      };
    } else if (stock < 50) {
      return {
        class: 'vc-delivery-status--low-stock',
        text: 'Fast ausverkauft'
      };
    } else {
      return {
        class: 'vc-delivery-status--in-stock',
        text: 'Sofort lieferbar'
      };
    }
  }

  function createBadgeHTML(statusText) {
    return `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
        <path d="M32 160C32 124.7 60.7 96 96 96L384 96C419.3 96 448 124.7 448 160L448 192L498.7 192C515.7 192 532 198.7 544 210.7L589.3 256C601.3 268 608 284.3 608 301.3L608 448C608 483.3 579.3 512 544 512L540.7 512C530.3 548.9 496.3 576 456 576C415.7 576 381.8 548.9 371.3 512L268.7 512C258.3 548.9 224.3 576 184 576C143.7 576 109.8 548.9 99.3 512L96 512C60.7 512 32 483.3 32 448L32 160zM544 352L544 301.3L498.7 256L448 256L448 352L544 352zM224 488C224 465.9 206.1 448 184 448C161.9 448 144 465.9 144 488C144 510.1 161.9 528 184 528C206.1 528 224 510.1 224 488zM456 528C478.1 528 496 510.1 496 488C496 465.9 478.1 448 456 448C433.9 448 416 465.9 416 488C416 510.1 433.9 528 456 528z"/>
      </svg>
      <span>${statusText}</span>
    `;
  }

  function updateStatus(container) {
    const isModal = container.classList.contains('quick-add-modal');

    const priceElement = isModal
      ? container.querySelector('.quick-add-modal__content product-price')
      : container.querySelector('product-price');

    if (!priceElement) return;

    const productForm = container.querySelector('product-form-component');
    if (!productForm) return;

    const variantId = productForm.querySelector('[name="id"]')?.value;
    if (!variantId) return;

    const stock = getStockLevel(container, variantId);
    const productTags = productForm.dataset.productTags || '';
    const isEOL = productTags.includes('EOL');

    const status = determineStatus(stock, isEOL);

    let badge = priceElement.parentNode.querySelector('.vc-delivery-status');

    if (badge) {
      badge.className = `vc-delivery-status ${status.class}`;
      badge.querySelector('span').textContent = status.text;
    } else {
      badge = document.createElement('div');
      badge.className = `vc-delivery-status ${status.class}`;
      badge.innerHTML = createBadgeHTML(status.text);
      priceElement.parentNode.insertBefore(badge, priceElement);
    }
  }

  function setupModalObservers(container) {
    // Beobachte JSON Script Tag
    const modalProductScript = container.querySelector('script[type="application/json"]');
    if (modalProductScript) {
      const jsonObserver = new MutationObserver(() => {
        updateStatus(container);
      });

      jsonObserver.observe(modalProductScript, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }

    // Beobachte Varianten-ID Input
    const productForm = container.querySelector('product-form-component');
    if (productForm) {
      const variantIdInput = productForm.querySelector('[name="id"]');
      if (variantIdInput) {
        const variantIdObserver = new MutationObserver(() => {
          setTimeout(() => updateStatus(container), 50);
        });

        variantIdObserver.observe(variantIdInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }
  }

  function setupProductPageObservers(container) {
    const inventoryElement = container.querySelector('product-inventory');
    if (inventoryElement) {
      const observer = new MutationObserver(() => {
        updateStatus(container);
      });

      observer.observe(inventoryElement, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }
  }

  function add(container) {
    const isModal = container.classList.contains('quick-add-modal');

    updateStatus(container);

    if (isModal) {
      setupModalObservers(container);
    } else {
      setupProductPageObservers(container);
    }
  }

  function initProductPage() {
    const productSection = document.querySelector('.product-information');
    if (productSection) {
      add(productSection);
    }
  }

  return {
    add,
    initProductPage
  };
})();


/* ========================================
   2. PAYMENT BUTTON CUSTOMIZATION
   ======================================== */

const PaymentButton = (() => {
  function customize() {
    const paymentButtons = document.querySelectorAll('.shopify-payment-button__button');

    paymentButtons.forEach(button => {
      if (button.dataset.vcCustomized) return;
      button.dataset.vcCustomized = 'true';

      const icon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" style="width: 20px; height: 20px; flex-shrink: 0;"><path fill="currentColor" d="M9.71,11.29a1,1,0,0,0-1.42,1.42l3,3A1,1,0,0,0,12,16h0a1,1,0,0,0,.72-.34l7-8a1,1,0,0,0-1.5-1.32L12,13.54Z"/><path fill="currentColor" d="M21,11h0a1,1,0,0,0-1,1,8,8,0,0,1-8,8h0A8,8,0,0,1,6.33,6.36,7.93,7.93,0,0,1,12,4a8.79,8.79,0,0,1,1.9.22,1,1,0,1,0,.47-1.94A10.54,10.54,0,0,0,12,2,10,10,0,0,0,5,19.09,9.93,9.93,0,0,0,12,22h0A10,10,0,0,0,22,12,1,1,0,0,0,21,11Z"/></svg>`;
      const newText = 'Artikel sofort kaufen';
      const content = `<span style="display: inline-flex; align-items: center; justify-content: center; width: 100%; gap: 8px;">${icon}<span style="line-height: 1;">${newText}</span></span>`;

      const span = button.querySelector('span');
      if (span) {
        span.outerHTML = content;
      } else {
        button.innerHTML = content;
      }
    });
  }

  function initObserver() {
    const observer = new MutationObserver(() => {
      customize();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  return {
    customize,
    initObserver
  };
})();


/* ========================================
   3. PRODUCT PAGE FOOTNOTE
   ======================================== */

const ProductPageFootnote = (() => {
  function add() {
    const productInfo = document.querySelector('.product-information');
    if (!productInfo) return;

    if (productInfo.querySelector('.vc-modal-footnote')) return;

    const groupBlockContent = productInfo.querySelector('.product-details .group-block .group-block-content');
    if (!groupBlockContent) return;

    const footnote = document.createElement('div');
    footnote.className = 'vc-modal-footnote';
    footnote.innerHTML = `
      <p>* Alle Preise abhängig vom gewährten Mengenrabatt, zzgl. <a href="/pages/versandbedingungen">Versandkosten</a>, Versandkostenfrei ab 100,- € Warenwert.</p>
    `;

    groupBlockContent.appendChild(footnote);
  }

  return {
    add
  };
})();


/* ========================================
   4. STICKY BUY BAR
   ======================================== */

const StickyBuyBar = (() => {

  let stickyBar = null;
  let addToCartButtons = null;
  let lastScrollY = window.scrollY;
  let mainProductForm = null;

  function isInModal() {
    // Check if we're inside a Quick-Add modal
    return document.querySelector('.quick-add-modal[open]') !== null;
  }

  function shouldShowStickyBar() {
    if (!addToCartButtons || addToCartButtons.length === 0) return false;

    // Find the first visible add-to-cart button
    for (const button of addToCartButtons) {
      const rect = button.getBoundingClientRect();
      // Check if button is above viewport (negative top position)
      if (rect.top < 0) {
        return true;
      }
    }
    return false;
  }

  function updateStickyBarVisibility() {
    if (!stickyBar || isInModal()) return;

    const shouldShow = shouldShowStickyBar();

    if (shouldShow) {
      stickyBar.setAttribute('data-visible', 'true');
    } else {
      stickyBar.setAttribute('data-visible', 'false');
    }
  }

  function handleScroll() {
    const currentScrollY = window.scrollY;

    // Only update if scroll position changed significantly (performance)
    if (Math.abs(currentScrollY - lastScrollY) > 10) {
      updateStickyBarVisibility();
      lastScrollY = currentScrollY;
    }
  }

  function init() {

    // Only run on product pages (not in modals)
    if (!document.querySelector('.product-information')) {
      return;
    }

    if (isInModal()) {
      return;
    }

    stickyBar = document.getElementById('vc-sticky-buy-bar');
    if (!stickyBar) {
      return;
    }

    // Find all add-to-cart buttons in the main product section
    addToCartButtons = document.querySelectorAll('.product-information [name="add"]:not([form*="sticky"])');

    if (!addToCartButtons || addToCartButtons.length === 0) {
      return;
    }

    // Check on load
    updateStickyBarVisibility();

    // Listen to scroll with throttling via requestAnimationFrame
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Recheck on resize
    window.addEventListener('resize', () => {
      updateStickyBarVisibility();
    });

    // Setup sticky bar functionality
    setupStickyBarFunctionality();
  }

  function setupStickyBarFunctionality() {
    // Get main product form
    mainProductForm = document.querySelector('.product-information product-form-component');
    if (!mainProductForm) return;

    const stickyForm = stickyBar.querySelector('form');
    if (!stickyForm) return;

    // 1. Quantity Buttons
    setupQuantityButtons();

    // 2. Variant Selector
    setupVariantSelector();

    // 3. Add to Cart (AJAX)
    setupAddToCart(stickyForm);

    // 4. Quick Buy Button
    setupQuickBuy();
  }

  function setupQuantityButtons() {
    const minusBtn = stickyBar.querySelector('.vc-sticky-quantity-button--minus');
    const plusBtn = stickyBar.querySelector('.vc-sticky-quantity-button--plus');
    const quantityInput = stickyBar.querySelector('#vc-sticky-quantity-input');

    if (!minusBtn || !plusBtn || !quantityInput) return;

    minusBtn.addEventListener('click', () => {
      const currentValue = parseInt(quantityInput.value) || 1;
      if (currentValue > 1) {
        quantityInput.value = currentValue - 1;
      }
    });

    plusBtn.addEventListener('click', () => {
      const currentValue = parseInt(quantityInput.value) || 1;
      quantityInput.value = currentValue + 1;
    });
  }

  function setupVariantSelector() {
    const variantSelect = stickyBar.querySelector('#vc-sticky-variant-select');
    if (!variantSelect) return;

    const hiddenVariantInput = stickyBar.querySelector('#vc-sticky-variant-id');
    if (!hiddenVariantInput) return;

    variantSelect.addEventListener('change', (e) => {
      hiddenVariantInput.value = e.target.value;

      // Sync with main product form
      if (mainProductForm) {
        const mainVariantInput = mainProductForm.querySelector('[name="id"]');
        if (mainVariantInput) {
          mainVariantInput.value = e.target.value;
          mainVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });

    // Listen to main form changes and sync back
    if (mainProductForm) {
      const mainVariantInput = mainProductForm.querySelector('[name="id"]');
      if (mainVariantInput) {
        mainVariantInput.addEventListener('change', (e) => {
          variantSelect.value = e.target.value;
          hiddenVariantInput.value = e.target.value;
        });
      }
    }
  }

  function setupAddToCart(form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const formData = new FormData(form);
      const addButton = form.querySelector('button[name="add"]');

      // Disable button
      addButton.disabled = true;
      addButton.classList.add('loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          const addedItem = await response.json();

          // Fetch updated cart state
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();

          // Dispatch Horizon Theme cart update event
          const event = new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: 'vc-sticky-buy-bar',
              data: {
                itemCount: cart.item_count,
                source: 'sticky-buy-bar',
                variantId: addedItem.variant_id,
                productId: addedItem.product_id
              }
            }
          });
          document.dispatchEvent(event);

        } else {
          const errorData = await response.json();
          alert(errorData.description || 'Fehler beim Hinzufügen zum Warenkorb');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        alert('Fehler beim Hinzufügen zum Warenkorb');
      } finally {
        addButton.disabled = false;
        addButton.classList.remove('loading');
      }
    });
  }

  function setupQuickBuy() {
    const quickBuyBtn = stickyBar.querySelector('#vc-sticky-quick-buy');
    if (!quickBuyBtn) return;

    quickBuyBtn.addEventListener('click', async () => {
      const stickyForm = stickyBar.querySelector('form');
      if (!stickyForm) return;

      const formData = new FormData(stickyForm);
      quickBuyBtn.disabled = true;

      try {
        // Add to cart first
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (response.ok) {
          // Redirect to checkout
          window.location.href = '/checkout';
        } else {
          alert('Fehler beim Hinzufügen zum Warenkorb');
        }
      } catch (error) {
        console.error('Quick buy error:', error);
        alert('Fehler beim Hinzufügen zum Warenkorb');
      } finally {
        quickBuyBtn.disabled = false;
      }
    });
  }


  return {
    init: init
  };
})();


/* ========================================
   5. MAKE FUNCTIONS GLOBALLY AVAILABLE
   ======================================== */

window.VCProducts = {
  DeliveryStatus: DeliveryStatus,
  PaymentButton: PaymentButton,
  ProductPageFootnote: ProductPageFootnote,
  StickyBuyBar: StickyBuyBar
};


/* ========================================
   6. INITIALIZATION
   ======================================== */

(function() {
  function init() {
    DeliveryStatus.initProductPage();
    PaymentButton.customize();
    PaymentButton.initObserver();
    ProductPageFootnote.add();
    StickyBuyBar.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
